import type { Task } from '@/types';

/**
 * 获取任务链中的所有后续任务
 * 同时考虑 predecessor 和 startNode 两种依赖关系
 * @param taskId 当前任务ID
 * @param tasks 所有任务列表
 * @returns 后续任务列表（按依赖顺序）
 */
export function getDownstreamTasks(taskId: string, tasks: Task[]): Task[] {
  const downstream: Task[] = [];
  const visited = new Set<string>();
  
  // 构建任务名称到任务的映射
  const taskByName = new Map<string, Task>();
  tasks.forEach(t => taskByName.set(t.name, t));
  
  // 构建 startNode 到任务列表的映射
  const startNodeToTasks = new Map<string, Task[]>();
  tasks.forEach(t => {
    if (t.startNode) {
      if (!startNodeToTasks.has(t.startNode)) {
        startNodeToTasks.set(t.startNode, []);
      }
      startNodeToTasks.get(t.startNode)!.push(t);
    }
  });
  
  function findDownstream(currentId: string) {
    // 找到当前任务
    const current = tasks.find(t => t.id === currentId);
    if (!current) return;
    
    // 使用任务名称作为visited的key，避免重复添加同名的任务
    const taskKey = current.name;
    if (visited.has(taskKey)) return;
    visited.add(taskKey);
    
    // 1. 找到所有以后置任务名称为 predecessor 的任务
    const predecessorDependents = tasks.filter(t => {
      if (current.name && t.predecessor === current.name) return true;
      return false;
    });
    
    // 2. 找到所有以当前任务名称为 startNode 的任务
    const startNodeDependents = startNodeToTasks.get(current.name) || [];
    
    // 合并两种依赖的任务
    const allDependents = [...predecessorDependents, ...startNodeDependents];
    
    // 去重（基于任务ID）
    const uniqueDependents = allDependents.filter((task, index, self) => 
      index === self.findIndex(t => t.id === task.id)
    );
    
    uniqueDependents.forEach(task => {
      downstream.push(task);
      findDownstream(task.id);
    });
  }
  
  findDownstream(taskId);
  
  return downstream;
}

/**
 * 计算日期差（天数）
 */
export function getDaysDifference(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * 调整任务链
 * @param changedTask 被修改的任务
 * @param newEndDate 新的结束日期
 * @param tasks 所有任务列表
 * @returns 需要更新的任务列表
 */
export function adjustTaskChain(
  changedTask: Task,
  newEndDate: string,
  tasks: Task[]
): Task[] {
  const updates: Task[] = [];
  
  // 计算变化量（天数）
  const daysDiff = getDaysDifference(changedTask.end, newEndDate);
  if (daysDiff === 0) return updates;
  
  // 获取所有后续任务
  const downstreamTasks = getDownstreamTasks(changedTask.id, tasks);
  
  // 调整每个后续任务的开始和结束日期
  downstreamTasks.forEach(task => {
    const newStart = addDays(task.start, daysDiff);
    const newEnd = addDays(task.end, daysDiff);
    
    updates.push({
      ...task,
      start: newStart,
      end: newEnd,
    });
  });
  
  return updates;
}

/**
 * 添加天数到日期
 */
function addDays(dateStr: string, days: number): string {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

/**
 * 检查任务链是否有效（无循环依赖）
 */
export function isValidTaskChain(tasks: Task[]): boolean {
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  
  function hasCycle(taskId: string): boolean {
    if (recursionStack.has(taskId)) return true;
    if (visited.has(taskId)) return false;
    
    visited.add(taskId);
    recursionStack.add(taskId);
    
    const task = tasks.find(t => t.id === taskId);
    if (task?.predecessor) {
      if (hasCycle(task.predecessor)) return true;
    }
    
    recursionStack.delete(taskId);
    return false;
  }
  
  for (const task of tasks) {
    if (hasCycle(task.id)) return false;
  }
  
  return true;
}

/**
 * 获取任务的完整路径（从起始任务到当前任务）
 */
export function getTaskPath(taskId: string, tasks: Task[]): Task[] {
  const path: Task[] = [];
  const visited = new Set<string>();
  
  function buildPath(currentId: string) {
    if (visited.has(currentId)) return;
    visited.add(currentId);
    
    const task = tasks.find(t => t.id === currentId);
    if (!task) return;
    
    if (task.predecessor) {
      buildPath(task.predecessor);
    }
    
    path.push(task);
  }
  
  buildPath(taskId);
  return path;
}
